import {
  Badge,
  BadgeProps,
  Box,
  Collapsible,
  HStack,
  Icon,
  Separator,
  SeparatorProps,
  Stack,
  Text,
} from '@chakra-ui/react'
import { ButtonLink, ButtonLinkProps } from '@src/components/sco/ButtonLink'
import { usePathname } from 'next/navigation'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { LuChevronDown, LuChevronUp } from 'react-icons/lu'

export type SidebarNestedNavListProps = {
  accordionBehavior?: 'navigate-and-expand' | 'expand-only'
  /** Tighter sub-link spacing for dense navs (the admin shell). Off by default. */
  compact?: boolean
  onClick?: () => void
  collapsed?: boolean
  expandOnHover?: boolean
  sections: Array<SidebarNestedNavListSection>
  navLinkProps?: Partial<NavLinkProps>
  subNavLinkProps?: Partial<SubNavLinkProps>
}
export type SidebarNestedNavListItem = {
  activePaths?: string[]
  activeExact?: boolean
  badgeProps?: BadgeProps
  buttonProps?: ButtonLinkProps
  customRender?: () => ReactNode
  dividerProps?: SeparatorProps
  href?: string
  isActive?: boolean
  title?: string | ReactNode
}

export type SidebarNestedNavListSection = {
  activePaths?: string[]
  activeExact?: boolean
  badgeProps?: BadgeProps
  buttonProps?: ButtonLinkProps
  dividerProps?: SeparatorProps
  customRender?: () => ReactNode
  href?: string
  icon?: any
  isOpen?: boolean
  isActive?: boolean
  title?: string | ReactNode
  items?: Array<SidebarNestedNavListItem>
}
type NavLinkProps = {
  accordionBehavior?: 'navigate-and-expand' | 'expand-only'
  activePaths?: string[]
  activeExact?: boolean
  badgeProps?: BadgeProps | null
  buttonProps?: ButtonLinkProps
  children: ReactNode | ReactNode[]
  compact?: boolean
  customRender?: () => ReactNode
  dividerProps?: SeparatorProps
  expandOnHover?: boolean
  href: string
  icon?: any
  isActive?: boolean
  isHoverExpanded?: boolean
  isOpen?: boolean
  onClick?: () => any
  onHoverEnd?: () => void
  onHoverStart?: () => void
  items?: Array<SidebarNestedNavListItem>

  activeBackgroundColor?: string
  activeColor?: string
  hoverBackgroundColor?: string
  hoverColor?: string
  inactiveBackgroundColor?: string
  inactiveColor?: string
  subNavLinkProps?: Partial<NavLinkProps>
}

const NavItemBadge = ({ badgeProps }: { badgeProps?: BadgeProps | null }) => {
  if (!badgeProps) {
    return null
  }

  return (
    <Badge flexShrink={0} size={'sm'} {...badgeProps}>
      {badgeProps.children || badgeProps.title}
    </Badge>
  )
}
type SubNavProps = {
  items?: Array<SubNavItem>
  collapsed?: boolean
  compact?: boolean
  onClick?: () => void
  subNavLinkProps?: Partial<NavLinkProps>
}
type SubNavItem = SidebarNestedNavListItem & {
  badge?: string
  activeBackgroundColor?: string
  activeColor?: string
  inactiveBackgroundColor?: string
  inactiveColor?: string
}

const HOVER_EXPAND_MS = 60
const HOVER_COLLAPSE_MS = 220

export const SidebarNestedNavList = (props: SidebarNestedNavListProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const expandTimerRef = useRef(0)
  const collapseTimerRef = useRef(0)
  const lastPointerRef = useRef({ x: Number.NaN, y: Number.NaN })
  const ignoreShiftRef = useRef(false)

  useEffect(() => {
    return () => {
      window.clearTimeout(expandTimerRef.current)
      window.clearTimeout(collapseTimerRef.current)
    }
  }, [])

  const markRealPointerMove = (event: React.MouseEvent) => {
    const dx = event.clientX - lastPointerRef.current.x
    const dy = event.clientY - lastPointerRef.current.y
    const moved = Number.isNaN(lastPointerRef.current.x) || dx * dx + dy * dy >= 9
    lastPointerRef.current = { x: event.clientX, y: event.clientY }
    if (moved) {
      ignoreShiftRef.current = false
    }
  }

  const hoverSection = (index: number) => {
    if (ignoreShiftRef.current) {
      return
    }
    window.clearTimeout(collapseTimerRef.current)
    window.clearTimeout(expandTimerRef.current)
    expandTimerRef.current = window.setTimeout(() => {
      setHoveredIndex(index)
      ignoreShiftRef.current = true
    }, HOVER_EXPAND_MS)
  }

  const leaveSection = (index: number) => {
    if (ignoreShiftRef.current) {
      return
    }
    window.clearTimeout(expandTimerRef.current)
    collapseTimerRef.current = window.setTimeout(() => {
      setHoveredIndex(current => (current === index ? null : current))
      ignoreShiftRef.current = true
    }, HOVER_COLLAPSE_MS)
  }

  return (
    <Stack
      backgroundColor={'transparent'}
      direction={'column'}
      gap={0}
      justify={'stretch'}
      justifyContent={'center'}
      width={'full'}
      onMouseMove={props.expandOnHover ? markRealPointerMove : undefined}
    >
      {props.sections.map((section, index) => (
        <NavLink
          key={index}
          accordionBehavior={props.accordionBehavior}
          activeExact={section.activeExact}
          activePaths={section.activePaths}
          compact={props.compact}
          customRender={section.customRender}
          dividerProps={section.dividerProps}
          href={section.href || '#'}
          icon={section.icon}
          isActive={section.isActive}
          isOpen={section.isOpen}
          items={section.items}
          subNavLinkProps={props.subNavLinkProps}
          {...props.navLinkProps}
          badgeProps={section.badgeProps ?? props.navLinkProps?.badgeProps}
          buttonProps={section.buttonProps ?? props.navLinkProps?.buttonProps}
          isHoverExpanded={props.expandOnHover ? hoveredIndex === index : false}
          onHoverEnd={props.expandOnHover ? () => leaveSection(index) : undefined}
          onHoverStart={props.expandOnHover ? () => hoverSection(index) : undefined}
        >
          {section.title}
        </NavLink>
      ))}
    </Stack>
  )
}

const NavLink = (props: NavLinkProps) => {
  const pathname = usePathname()
  const pathIsActive = !![props.href, ...(props.activePaths || [])]
    .filter(Boolean)
    .find(path => (!props.activeExact && path.length > 1 ? pathname?.startsWith(path) : pathname === path))
  // Explicit isActive (including false) wins over path matching so deep-links with query
  // params can deactivate a base path (e.g. /advisors/upgrade?advisorKey=…).
  const isActive = typeof props.isActive === 'boolean' ? props.isActive : pathIsActive

  const [accordionIsOpen, setAccordionIsOpen] = useState<boolean>(Boolean(props.isOpen || isActive))

  useEffect(() => {
    if (accordionIsOpen && isActive) {
      setAccordionIsOpen(false)
    }
  }, [isActive])

  const isPinnedOpen = accordionIsOpen || isActive || Boolean(props.isOpen)
  const isExpanded = isPinnedOpen || Boolean(props.isHoverExpanded)

  const onClick = e => {
    if (props.items && props.items.length > 0 && props.accordionBehavior === 'expand-only') {
      e.stopPropagation()
      e.preventDefault()
      setAccordionIsOpen(!accordionIsOpen)
      return false
    }
    if (props.onClick) {
      props.onClick()
    }
  }

  const isAccordion = props.items && props.items.length

  if (props.customRender) {
    return <>{props.customRender()}</>
  }

  if (props.dividerProps) {
    return <Separator marginY={1} {...(props.dividerProps || {})} />
  }
  return (
    <Box width={'full'} onMouseEnter={props.onHoverStart} onMouseLeave={props.onHoverEnd}>
      <ButtonLink
        bgColor={isActive ? 'colorPalette.emphasized/70' : null}
        fontWeight={isActive ? 'semibold' : 'medium'}
        href={props.href}
        justifyContent={'flex-start'}
        paddingX={3}
        paddingY={6}
        size={'md'}
        variant={'ghost'}
        width={'full'}
        onClick={onClick}
        {...props.buttonProps}
      >
        <HStack as={'span'} flex={'1'} textAlign={'left'} width={'full'}>
          {props.icon && <Icon as={props.icon} boxSize={4} />}
          <Text as={'span'} flexGrow={1} width={'full'}>
            {props.children}
          </Text>
          <NavItemBadge badgeProps={props.badgeProps} />
          {isAccordion && (isExpanded ? <Icon as={LuChevronUp} /> : <Icon as={LuChevronDown} />)}
        </HStack>
      </ButtonLink>
      {props.items && (
        <Collapsible.Root open={isExpanded} width={'full'}>
          <Collapsible.Content width={'full'}>
            <SubNav compact={props.compact} items={props.items} subNavLinkProps={props.subNavLinkProps} />
          </Collapsible.Content>
        </Collapsible.Root>
      )}
    </Box>
  )
}

export const SubNav = (props: SubNavProps) => {
  return (
    <Stack align={'stretch'} direction={'column'} gap={0} justify={'stretch'} marginY={1}>
      {props.items.map((section, index) => (
        <SubNavLink
          key={index}
          activeExact={section.activeExact}
          activePaths={section.activePaths}
          buttonProps={section.buttonProps}
          compact={props.compact}
          customRender={section.customRender}
          dividerProps={section.dividerProps}
          href={section.href || '#'}
          isActive={section.isActive}
          {...props.subNavLinkProps}
          badgeProps={section.badgeProps ?? props.subNavLinkProps?.badgeProps}
        >
          {section.title}
        </SubNavLink>
      ))}
    </Stack>
  )
}

type SubNavLinkProps = {
  activePaths?: string[]
  activeExact?: boolean
  badgeProps?: BadgeProps | null
  customRender?: () => ReactNode
  backgroundColor?: string
  children: ReactNode | ReactNode[]
  compact?: boolean
  dividerProps?: SeparatorProps
  href: string
  isActive?: boolean
  onClick?: () => any

  activeBackgroundColor?: string
  activeColor?: string
  hoverColor?: string
  hoverBackgroundColor?: string
  inactiveBackgroundColor?: string
  inactiveColor?: string
  buttonProps?: ButtonLinkProps
}

const SubNavLink = (props: SubNavLinkProps) => {
  const pathname = usePathname()

  const pathIsActive = !![props.href, ...(props.activePaths || [])]
    .filter(Boolean)
    .find(path => (!props.activeExact && path.length > 1 ? pathname?.startsWith(path) : pathname === path))
  // Explicit isActive (including false) wins over path matching, matching NavLink.
  const isActive = typeof props.isActive === 'boolean' ? props.isActive : pathIsActive

  const onClick = () => {
    if (props.onClick) {
      props.onClick()
    }
  }

  if (props.customRender) {
    return <>{props.customRender()}</>
  }

  if (props.dividerProps) {
    return <Separator {...props.dividerProps} />
  }

  return (
    <ButtonLink
      bgColor={isActive ? 'colorPalette.emphasized/30' : null}
      border={0}
      borderLeftRadius={0}
      display={'flex'}
      flex={1}
      href={props.href}
      marginLeft={3.5}
      padding={0}
      size={'sm'}
      variant={'ghost'}
      width={'full'}
      onClick={onClick}
      {...props.buttonProps}
    >
      <Box
        borderLeft={'2px solid'}
        borderLeftColor={'colorPalette.border'}
        fontSize={'sm'}
        paddingLeft={props.compact ? 3 : 4}
        paddingY={props.compact ? 1.5 : 2}
        width={'full'}
      >
        {props.badgeProps ? (
          <HStack align={'center'} gap={2} justify={'space-between'} width={'full'}>
            <Box flex={'1'} minWidth={0}>
              {props.children}
            </Box>
            <NavItemBadge badgeProps={props.badgeProps} />
          </HStack>
        ) : (
          props.children
        )}
      </Box>
    </ButtonLink>
  )
}
